// What the Connections page knows without rendering anything: the four status words, how a card is grouped,
// how a catalog entry connects, and the sentences the page says when something fails. Pure functions only,
// so the components below only draw.

import { AlertCircle, Check, Lock, Plug } from "lucide-react";

// Rendered exactly as the contract says: four words, one tone each. `not_connected` never becomes a card.
export const STATUS_META = Object.freeze({
  connected: { label: "Connected", tone: "good", Icon: Check },
  not_working: { label: "Not working", tone: "bad", Icon: AlertCircle },
  not_verified: { label: "Saved", tone: "quiet", Icon: Lock },
  not_connected: { label: "Not connected", tone: "quiet", Icon: Plug },
});

export function stateMeta(status) {
  return STATUS_META[status] || STATUS_META.not_connected;
}

export function cardMeta(card) {
  return stateMeta(card?.status);
}

/** A key app the sweep cannot probe: on the agent, never checked, nothing wrong. */
export function isUncheckableKey(card) {
  return card?.kind === "api_key" && card?.canTest === false && card?.status === "not_verified";
}

export function describeChecked(iso, now = new Date()) {
  if (!iso) return "Never checked";
  const minutes = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "Checked just now";
  if (minutes < 60) return `Checked ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `Checked ${hours} hour${hours === 1 ? "" : "s"} ago`;
  return `Checked ${Math.round(hours / 24)} days ago`;
}

/** "12 min ago", for a footer that has no room for a sentence. */
export function shortAgo(iso, now = new Date()) {
  if (!iso) return "never";
  const minutes = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

// How each kind of card got onto the agent, in the client's words.
export const KIND_WORDS = Object.freeze({
  mcp: "Signed in",
  higgsfield: "Signed in",
  included: "Included with GrowthOS",
  pipedream: "Linked account",
  api_key: "Key on the agent",
});

export function kindWord(card) {
  return KIND_WORDS[card?.kind] || "Connection";
}

// The three groups of the page. Order matters: what the agent signed into itself comes first.
export const GROUPS = Object.freeze([
  { key: "signed_in", kinds: ["mcp", "higgsfield"], title: (agent) => `Signed in with ${agent}` },
  { key: "linked", kinds: ["pipedream"], title: () => "Linked accounts" },
  { key: "keys", kinds: ["api_key"], title: () => "Keys" },
]);

/** Drops anything the page never shows. The server's order is kept. */
export function visibleCards(cards = []) {
  return cards.filter((card) => card && card.status !== "not_connected");
}

/**
 * Cards by kind, in the contract's three groups, the server's order kept inside each. A kind nobody
 * expected lands in Keys rather than disappearing. Empty groups are left out.
 */
export function groupCards(cards = [], agentName = "your agent") {
  const buckets = GROUPS.map((group) => ({ key: group.key, title: group.title(agentName), cards: [] }));
  for (const card of visibleCards(cards)) {
    const index = GROUPS.findIndex((group) => group.kinds.includes(card.kind));
    buckets[index === -1 ? buckets.length - 1 : index].cards.push(card);
  }
  return buckets.filter((bucket) => bucket.cards.length);
}

/** How many cards are in each state, for the line under the title. */
export function tally(cards = []) {
  const shown = visibleCards(cards);
  return {
    total: shown.length,
    connected: shown.filter((card) => card.status === "connected").length,
    problems: shown.filter((card) => card.status === "not_working").length,
    saved: shown.filter((card) => card.status === "not_verified").length,
  };
}

/** The one line under a card's name: the account it is, else how it got here. */
export function cardSubline(card) {
  return card?.accountLabel || kindWord(card);
}

/** What a card's footer says: when it was checked, and for a sign-in, how many tools came with it. */
export function cardFacts(card, now = new Date()) {
  // Nothing to check on an included app: it runs on GrowthOS's account and is paid from the Alloy credits.
  if (card?.kind === "included") return [card.note || "Paid from your Alloy tokens"];
  const facts = [];
  if (!isUncheckableKey(card)) facts.push(describeChecked(card?.lastCheckedAt, now));
  if (card?.kind === "mcp" && Number.isFinite(card?.toolCount)) facts.push(`${card.toolCount} tools`);
  if (card?.kind === "higgsfield" && Number.isFinite(card?.toolCount)) facts.push(`${card.toolCount} tools`);
  return facts;
}

export const JOB_WORDS = Object.freeze({ install: "Installing", remove: "Removing", test: "Checking", handoff: "Submitted" });

export function isWorking(job) {
  return Boolean(job && ["queued", "applying"].includes(job.status));
}

/** The sentence a running or failed job puts on its card, or null when the job is done. */
export function jobLine(job) {
  if (!job || job.status === "done") return null;
  const word = JOB_WORDS[job.action] || "Working";
  if (job.status === "failed") return `${word} failed. ${job.error || ""}`.trim();
  return `${word}…`;
}

// The ways a catalog entry connects, named the way the Add panel shows them.
export const ROUTE_WORDS = Object.freeze({
  mcp: "Sign in",
  pipedream: "Link account",
  chat: "Ask your agent",
  key: "With a key",
  shopify_partner: "We set it up",
  shopify_oauth: "Sign in",
  meta_signin: "Sign in",
  google_ads_customer: "Sign in",
  higgsfield: "Sign in",
  custom: "With a key",
});

export function routeWord(flow) {
  return ROUTE_WORDS[flow] || ROUTE_WORDS.key;
}

/** A catalog field under either name the catalog has used for it. */
export function fieldKey(field) {
  return field?.envKey || field?.name || "";
}

/** The fields a person is asked for: never an alias the agent merely accepts. */
export function askedFields(app) {
  return (app?.fields || []).filter((field) => field.ask !== "never");
}

export const KEY_FLOWS = Object.freeze(["key", "shopify_partner", "custom"]);
export function isKeyFlow(flow) { return KEY_FLOWS.includes(flow); }

/**
 * The Add panel's rows: every addable app, grouped by the catalog's own group word, minus anything on its way
 * to the agent. An app already on the agent is kept and flagged `connected`: a key app is offered again so its key
 * can be replaced; a sign-in or a link is offered again only from its card.
 */
export function addableChoices({ addable = [], connected = new Set(), pending = new Set() } = {}) {
  return addable
    .filter((entry) => entry && entry.app && !pending.has(entry.app))
    .map((entry) => ({ app: entry.app, label: entry.label || entry.app, group: entry.group || "Other", flow: entry.flow || "key", connected: connected.has(entry.app), entry }));
}

/** The rows the picker shows: everything not yet connected, plus connected key apps (to replace the key). */
export function pickerChoices(choices = []) {
  return choices.filter((choice) => !choice.connected || isKeyFlow(choice.flow));
}

/** What a picker row says: how it connects, or that it replaces a key already there. */
export function choiceRouteWord(choice) {
  return choice?.connected && isKeyFlow(choice.flow) ? "Replace key" : routeWord(choice?.flow);
}

export function filterChoices(choices = [], query = "") {
  const terms = String(query).toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return choices;
  return choices.filter((choice) => {
    const haystack = [choice.label, choice.app, choice.group, routeWord(choice.flow)].filter(Boolean).join(" ").toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}

/** Choices by their catalog group, the groups in first-seen order. */
export function groupChoices(choices = []) {
  const groups = new Map();
  for (const choice of choices) {
    const key = choice.group || "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(choice);
  }
  return [...groups.entries()].map(([title, items]) => ({ title, items }));
}

// The order ecommerce brands reach for them; anything else follows, in catalog order.
const POPULAR = ["shopify", "klaviyo", "meta_ads", "google_ads", "google_analytics", "amazon_seller", "notion", "hubspot", "asana", "canva", "higgsfield", "pinterest", "monday", "github"];
const rank = (app) => { const index = POPULAR.indexOf(String(app || "").toLowerCase()); return index === -1 ? POPULAR.length : index; };

/** The few tiles under the cards, most reached-for first; nothing already on the agent. */
export function popularChoices(choices = [], limit = 5) {
  const sorted = choices.filter((choice) => !choice.connected).sort((a, b) => rank(a.app) - rank(b.app));
  return { shown: sorted.slice(0, limit), total: sorted.length };
}

export function assertSameWorkspace(data, activeClient) {
  const expected = String(activeClient?.clientSlug || "");
  const actual = String(data?.client?.clientSlug || data?.clientSlug || "");
  if (expected && actual !== expected) {
    const error = new Error("The dashboard returned data for a different workspace.");
    error.code = actual ? "tenant_payload_mismatch" : "tenant_response_missing";
    throw error;
  }
  return data;
}

export function secureAuthorizationUrl(value) {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function clearConnectionCallbackQuery() {
  const url = new URL(window.location.href);
  window.history.replaceState({}, "", `${url.pathname}${url.hash}`);
}

// What each failure actually means, in the words the person can act on. Several codes share one sentence
// because they are one situation: a sign-in that has to finish where it started, or a service that is not
// configured for this brand yet.
const SIGN_IN_SERVICE_REFUSED = { title: "The sign-in service refused this app right now", message: "Try again in a minute; if it keeps failing, message X SCALE." };
const SIGN_IN_SAME_TAB = { title: "This sign-in must finish in the same browser tab and site it started in", message: "Start it again from here." };
const CONNECTIONS_UNAVAILABLE = { title: "Connections are unavailable right now", message: "X SCALE has been alerted; try again later." };
const GENERIC_ACTION_ERROR = { title: "Connection action failed", message: "The connection could not be updated. Your last verified state is still shown." };
const SIGN_IN_IN_PROGRESS = { title: "A sign-in is still in progress", message: "Finish it in the other window, or try again in a minute." };
const ACTION_ERROR_BY_CODE = {
  shopify_session_expired: { title: "The Shopify sign-in link expired", message: "Start again and finish approving in the same browser within ten minutes." },
  shopify_session_invalid: SIGN_IN_SAME_TAB,
  shopify_configuration_changed: { title: "Shopify setup changed", message: "Start the Shopify connection again to use the latest settings." },
  shopify_connection_unavailable: { title: "Shopify sign-in is not ready for this workspace", message: "Contact X SCALE to finish enabling Shopify sign-in." },
  shopify_connection_busy: { title: "Shopify is still being connected", message: "Wait for the current connection to finish, then refresh." },
  shopify_authorization_failed: { title: "Shopify authorization could not be completed", message: "Start again and approve access for the store shown in HUB. If it still fails, contact X SCALE." },
  shopify_install_failed: { title: "Shopify could not be installed on your agent", message: "The store approval succeeded, but the agent setup needs attention. Contact X SCALE." },
  shopify_store_check_failed: { title: "The Shopify store check did not pass", message: "The agent could not verify access to your store. Contact X SCALE before reconnecting." },
  shopify_origin_mismatch: SIGN_IN_SAME_TAB,
  shopify_signin_required: { title: "Connect Shopify by signing in", message: "Refresh this page and choose Connect Shopify." },
  connection_already_connected: { title: "Already connected", message: "This app is already signed in for your agent." },
  connection_authorization_in_progress: SIGN_IN_IN_PROGRESS,
  mcp_flow_in_progress: SIGN_IN_IN_PROGRESS,
  mcp_signin_in_progress: SIGN_IN_IN_PROGRESS,
  mcp_name_in_use: { title: "Already connected with a key", message: "This app is already connected with a key. Remove the key first to sign in instead." },
  mcp_not_enabled: { title: "Signing in to apps is not turned on yet", message: "Signing in to apps is not turned on for your agent yet. Contact X SCALE to enable it." },
  mcp_flow_expired: { title: "The sign-in expired", message: "The sign-in expired. Start it again." },
  mcp_signin_failed: { title: "The sign-in could not start", message: "The agent could not start the sign-in. Try again in a minute." },
  mcp_signin_busy: { title: "Too many sign-ins at once", message: "Too many sign-ins are in progress on the agent. Try again in a minute." },
  mcp_signin_refused: { title: "This app refuses a sign-in from the dashboard", message: "Ask your agent in chat: it can sign in from its own machine, or use a key where the app takes one." },
  mcp_add_failed: { title: "The app could not be added", message: "The agent could not add this app. Try again in a minute." },
  agent_unreachable: { title: "Your agent could not be reached", message: "Your agent could not be reached. Try again in a minute." },
  connection_owner_or_admin_required: { title: "Only an owner or admin can connect apps", message: "Only an owner or admin can connect apps. Ask one of them to do it." },
  chat_seed_invalid: { title: "This app cannot be set up in chat", message: "Your agent cannot set this app up in chat. Use the form on the Connections page." },
  connection_callback_actor_mismatch: { title: "Sign-in not allowed for this workspace", message: "Ask X SCALE to enable connections for this brand." },
  connection_provider_setup_required: SIGN_IN_SERVICE_REFUSED,
  connection_callback_origin_mismatch: SIGN_IN_SAME_TAB,
  connection_callback_cookie_invalid: SIGN_IN_SAME_TAB,
  higgsfield_connection_not_started: SIGN_IN_SAME_TAB,
  agent_connection_fields_invalid: { title: "One of the values is not in the expected format", message: "Check the key and try again." },
  agent_connection_key_name_reserved: { title: "That name belongs to an app you connect by signing in", message: "Pick the app from Add instead, or use a different key name." },
  google_ads_customer_id_required: { title: "Google Ads needs its customer id", message: "Enter the 10-digit customer id from Google Ads and start the sign-in again." },
  meta_signin_not_configured: { title: "The Meta sign-in is not available right now", message: "Sign in with Facebook is not set up on this dashboard yet. Try again later, or ask your agent." },
  agent_connections_unavailable: CONNECTIONS_UNAVAILABLE,
  client_auth_missing: { title: "Please sign in again", message: "Your dashboard session has ended. Sign in again, then retry this connection." },
  network_error: { title: "The connection service could not be reached", message: "Check your internet connection, then try again." },
  upstream_unavailable: { title: "The connection service is unavailable right now", message: "Please try again shortly. Your existing connections are unchanged." },
  tenant_payload_mismatch: { title: "Workspace response rejected", message: "The dashboard rejected a response for a different workspace. Refresh after confirming the active client." },
  tenant_response_missing: { title: "Workspace response rejected", message: "The dashboard rejected a response for a different workspace. Refresh after confirming the active client." },
};

/** The sentence for a failure whose code the page has one for, or null: anything else is not worth showing as it is. */
export function knownConnectionError(error) {
  return ACTION_ERROR_BY_CODE[String(error?.code || "")] ?? null;
}

// A native sign-in that ended without finishing, by how the dashboard says it ended (the flow's `outcome`): the person
// said no, the app refused the sign-in, it expired, or anything else. What the agent's machine or the provider said is
// never shown; it stays in the dashboard's log. The same four sentences as the dashboard's (agentMcp.ts).
const SIGN_IN_ENDED = Object.freeze({
  denied: () => "You cancelled the sign-in. Start again when you are ready.",
  refused: (label) => `${label} did not finish the sign-in. Start again.`,
  expired: () => ACTION_ERROR_BY_CODE.mcp_flow_expired.message,
  unknown: () => "The sign-in could not be finished. Start again.",
});

/** The sentence for a native sign-in that ended without finishing. `label` is the app's name. */
export function signInEndedMessage(outcome, label) {
  const say = Object.prototype.hasOwnProperty.call(SIGN_IN_ENDED, outcome) ? SIGN_IN_ENDED[outcome] : SIGN_IN_ENDED.unknown;
  return say(String(label || "").trim() || "The app");
}

export function describeConnectionActionError(error) {
  const code = String(error?.code || "");
  const known = knownConnectionError(error);
  if (known) return known;
  // Hermes refuses a second sign-in for the same server while one is open.
  if (error?.status === 409) return SIGN_IN_IN_PROGRESS;
  if (code.includes("inflight") || code.includes("processing")) {
    return { title: "Connection is still processing", message: "The current authorization attempt is still being verified. Wait a moment, then refresh this connection." };
  }
  if (code.includes("reauthor") || code.includes("revoked")) {
    return { title: "Reauthorization required", message: "Reconnect this provider to replace its expired or revoked authorization." };
  }
  const thrown = typeof error?.message === "string" ? error.message.trim() : "";
  if (!code && thrown) return { title: GENERIC_ACTION_ERROR.title, message: thrown };
  return GENERIC_ACTION_ERROR;
}

export function isSignInInProgress(error) {
  // A name a key already holds is a 409 too, but nothing is being signed in to: that one is permanent.
  if (error?.code === "mcp_name_in_use") return false;
  return error?.status === 409 || ["connection_authorization_in_progress", "mcp_flow_in_progress", "mcp_signin_in_progress"].includes(String(error?.code || ""));
}

// The tools shared with the brand this one is hosted with (a brand profile and its host, /api/agent-connections/
// sharing). The panel lists the partner's tools this brand can take (`from` the partner) first, then this brand's it
// can give (`to` the partner); each row knows its direction and the partner, so one click says what it does.
export function sharingRows(partner) {
  if (!partner) return [];
  const row = (tool, direction) => ({
    key: `${direction}:${tool.app}`, app: tool.app, label: tool.label || tool.app, kind: tool.kind, status: tool.status, reason: tool.reason ?? null,
    accountLabel: tool.accountLabel ?? null, way: tool.way, shared: Boolean(tool.shared), signsInOnAgent: Boolean(tool.signsInOnAgent), direction, partnerSlug: partner.clientSlug,
    partnerName: partner.clientName || partner.clientSlug,
  });
  return [...(partner.theirs || []).map((tool) => row(tool, "from")), ...(partner.mine || []).map((tool) => row(tool, "to"))];
}

/**
 * A partner's section as two lists: the partner's tools this brand can take (`theirs`) and this brand's it can give
 * (`mine`). A tool both brands hold shows once, in the list the page leads with (the host's tools on a brand profile's
 * page, its own on a host's), whose row already says the other brand has it: the other list's row would say it again.
 */
export function sharingLists(partner) {
  const rows = sharingRows(partner);
  const theirs = rows.filter((row) => row.direction === "from");
  const mine = rows.filter((row) => row.direction === "to");
  const leads = partner?.relation === "host" ? theirs : mine;
  const onBoth = new Set(leads.filter((row) => row.shared && row.status !== "not_connected").map((row) => row.app));
  const once = (list) => (list === leads ? list : list.filter((row) => !(row.shared && onBoth.has(row.app))));
  return { theirs: once(theirs), mine: once(mine) };
}

/**
 * What one click on a row does, or null when there is nothing to press: a key copied to the brand that lacks it (Use
 * it here, Share with the partner), a sign-in made once for that brand (Sign in here, Sign in for the partner), or,
 * once the tool is there, the word that says so, which everyone sees. A tool coming to this page's own brand says
 * "here", never that brand's name: the page is already that brand's.
 */
export function sharingAction(row, { activeName = "this brand", canManage = false, canConnect = canManage } = {}) {
  // A tool this brand has not linked (its own list names every one it can share) is linked here first: managing this
  // brand is enough for that.
  if (row.status === "not_connected") return row.direction === "to" && canConnect ? { kind: "connect", label: "Connect" } : null;
  const here = row.direction === "from";
  const holder = here ? row.partnerName : activeName;
  if (row.shared) return { kind: "shared", label: here ? "On this brand" : `On ${row.partnerName}` };
  if (!canManage) return null;
  // A key the holder's own check rejects is not copied: the copy would fail the same check on the other brand.
  if (row.way === "copy" && row.status === "not_working") return { kind: "blocked", label: `Fix it on ${holder} first` };
  if (row.way === "copy") return { kind: "copy", label: here ? "Use it here" : `Share with ${row.partnerName}` };
  return { kind: "sign_in", label: here ? "Sign in here" : `Sign in for ${row.partnerName}` };
}
